import { X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import IcBaselineTask from '../../assets/icons/ic_baseline-task.svg';
import MaterialSymbolsStarRounded from '../../assets/icons/material-symbols_star-rounded.svg';
import TdesignTaskAddFilled from '../../assets/icons/tdesign_task-add-filled.svg';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { typography } from '../../styles/typography';
import { employeeApi } from '../ShiftManagement/employeeApi';
import { getAllSites } from '../SiteManagement/siteApi';
import { Card, DonutChart, ProfileImage, SearchBar } from '../ui';
import { bestPerformerApi } from './BestPerformerApi';

const BestPerformer = () => {
  // Log screen view
  useScreenLogging('BestPerformer', {
    component: 'BestPerformer',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [showAllSitesDropdown, setShowAllSitesDropdown] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [allSites, setAllSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Function to fetch all sites from API
  const fetchAllSites = async () => {
    setSitesLoading(true);
    try {
      const response = await getAllSites();
      // Handle different possible response structures
      const sitesData = response.data || response.sites || response || [];
      // Sort sites alphabetically by site name
      const sortedSites = Array.isArray(sitesData) 
        ? [...sitesData].sort((a, b) => {
            const nameA = (a?.site_name || a?.name || '').toLowerCase();
            const nameB = (b?.site_name || b?.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
          })
        : [];
      setAllSites(sortedSites);
      console.log('Sites fetched successfully:', sortedSites);
    } catch (err) {
      console.error('Error fetching sites:', err);
      setAllSites([]);
    } finally {
      setSitesLoading(false);
    }
  };

  // Function to fetch user details for multiple user IDs using bulk API endpoints
  const fetchUserDetails = async (userIds) => {
    if (!userIds || userIds.length === 0) return {};
    
    try {
      // Fetch all users using the working bulk API endpoints
      const [guardsData, supervisorsData] = await Promise.all([
        employeeApi.fetchAvailableGuards().catch(error => {
          console.warn('Failed to fetch guards:', error);
          return [];
        }),
        employeeApi.fetchAvailableSupervisors().catch(error => {
          console.warn('Failed to fetch supervisors:', error);
          return [];
        })
      ]);
      
      // Combine all users into a single array
      const allUsers = [...guardsData, ...supervisorsData];
      
      // Create a map for quick lookup by user_id
      const userDetailsMap = {};
      allUsers.forEach(user => {
        const userId = user.user_id || user.employee_id;
        if (userId) {
          userDetailsMap[userId] = {
            userId,
            employee_id: user.employee_id || user.user_id,
            name: user.user_name || user.name || user.employee_name || 'Unknown',
            user_name: user.user_name || user.name || user.employee_name || 'Unknown',
            role_name: user.spec_role || user.role_name || 'Unknown',
            ...user
          };
        }
      });
      
      // For any user IDs not found in the bulk data, create Unknown entries
      userIds.forEach(userId => {
        if (!userDetailsMap[userId]) {
          userDetailsMap[userId] = {
            userId,
            employee_id: userId,
            name: 'Unknown',
            user_name: 'Unknown',
            role_name: 'Unknown'
          };
        }
      });
      
      console.log('🔍 Fetched user details map from bulk API:', userDetailsMap);
      return userDetailsMap;
    } catch (error) {
      console.error('Error fetching user details:', error);
      return {};
    }
  };


  // Function to fetch best performer data from API
  // This function is called when a site is selected or when month/year changes
  const fetchBestPerformerData = async (month, year) => {
    if (!selectedSite) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // First, fetch the best performer data from the API
      // Note: bestPerformerApi.getBestPerformerData already returns parsed JSON data (not a Response object)
      const data = await bestPerformerApi.getBestPerformerData(month, year);
      
      // Defensive check: ensure we received valid data
      if (!data) {
        throw new Error('No data received from API');
      }
      
      // Handle different possible response structures
      const performersData = data.data || data.bestPerformers || data || [];
      console.log('Best performer data fetched successfully:', performersData);
      
      // Collect all unique user IDs from the performer data
      const allUserIds = new Set();
      if (typeof performersData === 'object' && !Array.isArray(performersData)) {
        Object.keys(performersData).forEach(siteId => {
          const siteData = performersData[siteId];
          if (siteData && typeof siteData === 'object') {
            Object.keys(siteData).forEach(roleName => {
              const roleData = siteData[roleName];
              if (roleData && roleData.user_id) {
                allUserIds.add(roleData.user_id);
              }
            });
          }
        });
      }
      
      // Fetch user details for all unique user IDs
      const userDetailsMap = await fetchUserDetails(Array.from(allUserIds));
      console.log('🔍 Fetched user details map:', userDetailsMap);
      
      // Store both the original data and user details map
      setApiData({
        performersData: performersData,
        userDetailsMap: userDetailsMap
      });
      
    } catch (err) {
      console.error('Error fetching best performer data:', err);
      setError(err.message || 'Failed to fetch best performer data');
      setApiData(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter performers based on selected site and API data
  // Transform the nested API data structure into a flat array of performers
  const bestPerformers = React.useMemo(() => {
    if (!selectedSite || !apiData || !apiData.performersData) return [];
    
    console.log('Processing performers for site:', selectedSite);
    console.log('API data structure:', apiData);
    
    const performers = [];
    const performersData = apiData.performersData;
    const userDetailsMap = apiData.userDetailsMap || {};
    
    // Iterate through all site IDs in the API data
    Object.keys(performersData).forEach(siteId => {
      console.log('Checking site ID:', siteId, 'against selected site:', selectedSite.site_id || selectedSite.id);
      
      // Check if this site ID matches the selected site
      if (selectedSite.site_id === siteId || selectedSite.id === siteId) {
        const siteData = performersData[siteId];
        console.log('Found matching site data:', siteData);
        
        // Iterate through all roles for this site
        Object.keys(siteData).forEach(roleName => {
          const roleData = siteData[roleName];
          console.log('Processing role:', roleName, 'data:', roleData);
          
          // If role data exists and has performer information
          if (roleData && typeof roleData === 'object' && roleData.user_id) {
            // Get user details from the fetched employee data
            const userDetails = userDetailsMap[roleData.user_id] || {};
            
            const performer = {
              id: roleData.user_id,
              name: userDetails.name || roleData.user_name || 'Unknown',
              employeeId: userDetails.employee_id || roleData.user_id, // Use employee_id from employee API
              role: userDetails.role_name || roleData.display_name || roleData.spec_role || roleName,
              totalShifts: roleData.attendance_total || 0,
              presentShifts: roleData.attendance_present || 0,
              absentShifts: (roleData.attendance_total || 0) - (roleData.attendance_present || 0),
              totalTasks: roleData.tasks_total || 0,
              tasksOnTime: roleData.tasks_on_time || 0,
              taskScore: roleData.task_score || 0,
              attendanceScore: roleData.attendance_score || 0,
              totalScore: roleData.total_score || 0,
              attendancePercentage: roleData.attendance_total > 0 
                ? Math.round((roleData.attendance_present / roleData.attendance_total) * 100 * 10) / 10 
                : 0,
              profileImageUrl: null, // Profile images will be handled by ProfileImage component
              userName: userDetails.user_name || roleData.user_name || 'Unknown'
            };
            performers.push(performer);
            console.log('Added performer:', performer);
          }
        });
      }
    });
    
    console.log('Total performers found:', performers.length);
    
    // Sort by total score descending
    return performers.sort((a, b) => b.totalScore - a.totalScore);
  }, [selectedSite, apiData]);

  // Set first employee as selected when performers are loaded
  React.useEffect(() => {
    if (bestPerformers.length > 0 && !selectedEmployee) {
      setSelectedEmployee(bestPerformers[0]);
    }
  }, [bestPerformers, selectedEmployee]);

  // Detect mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch sites on component mount
  useEffect(() => {
    fetchAllSites();
  }, []);

  // Fetch data when site is selected or month/year changes
  useEffect(() => {
    if (selectedSite) {
      fetchBestPerformerData(currentMonth, currentYear);
    }
  }, [selectedSite, currentMonth, currentYear]);

  // Reset selected employee when data changes
  useEffect(() => {
    if (bestPerformers && bestPerformers.length > 0) {
      setSelectedEmployee(bestPerformers[0]);
    } else {
      setSelectedEmployee(null);
    }
  }, [bestPerformers]);

  // Handle search functionality
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (value.length > 0) {
      // Filter through real site data
      const filteredSites = allSites.filter(site => 
        site.site_name && site.site_name.toLowerCase().includes(value.toLowerCase())
      );
      setSearchSuggestions(filteredSites.map(site => site.site_name));
    } else {
      setSearchSuggestions([]);
    }
  };

  // Handle search bar focus - show all sites
  const handleSearchFocus = () => {
    setSearchSuggestions(allSites.map(site => site.site_name));
    setShowAllSitesDropdown(true);
  };

  // Handle search bar blur
  const handleSearchBlur = () => {
    // Delay hiding to allow clicking on suggestions
    setTimeout(() => {
      setShowAllSitesDropdown(false);
    }, 200);
  };

  // Show all sites when search bar is clicked/focused
  const handleSearchBarClick = () => {
    setSearchSuggestions(allSites.map(site => site.site_name));
    setShowAllSitesDropdown(true);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      const searchContainer = document.querySelector('.search-container');
      if (searchContainer && !searchContainer.contains(event.target)) {
        setShowAllSitesDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearchSubmit = (value) => {
    // Find the selected site from allSites
    const site = allSites.find(s => s.site_name === value);
    if (site) {
      setSelectedSite(site);
    }
    setShowAllSitesDropdown(false);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion);
    setSearchSuggestions([]);
    setShowAllSitesDropdown(false);
    // Find and set the selected site with full information
    const site = allSites.find(s => s.site_name === suggestion);
    if (site) {
      setSelectedSite(site);
    }
  };

  const clearSiteSelection = () => {
    setSelectedSite(null);
    setSearchTerm('');
    setSearchSuggestions([]);
    setShowAllSitesDropdown(false);
    setSelectedEmployee(null);
    setApiData(null);
    setError(null);
  };

  // Handle employee card click
  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee);
    if (isMobile) {
      setShowMobileModal(true);
    }
  };

  // Render employee details (reusable for desktop and mobile)
  const renderEmployeeDetails = (employee) => {
    if (!employee) return null;

    return (
      <div className="w-full h-full p-4 md:p-6 flex flex-col gap-4 md:gap-6">
        {/* Employee Profile */}
        <div className="w-full inline-flex flex-col sm:flex-row justify-start items-start gap-4 md:gap-6">
          <ProfileImage
            userId={employee.id}
            userName={employee.userName || employee.name}
            imagePath={employee.profileImageUrl}
            size="lg"
            className="w-20 h-20 md:w-24 md:h-24 flex-shrink-0"
          />
          <div className="w-full sm:flex-1 inline-flex flex-col justify-start items-start gap-2">
            <div className={`self-stretch justify-center text-black ${typography.h4} text-base md:text-lg`}>{employee.name}</div>
            <div className="inline-flex justify-start items-center gap-2 md:gap-3 flex-wrap">
              <div className={`justify-center text-black ${typography.h4} text-sm md:text-base`}>Role:</div>
              <div className={`justify-center text-black ${typography.p} text-sm md:text-base`}>{employee.role}</div>
            </div>
            <div className="inline-flex justify-start items-start gap-2 md:gap-3 flex-wrap">
              <div className={`justify-center text-black ${typography.h4} text-sm md:text-base`}>Total Score:</div>
              <div className={`justify-center text-black ${typography.p} text-sm md:text-base`}>{employee.totalScore}</div>
            </div>
          </div>
        </div>
        
        {/* Month/Year Display */}
        <div className={`w-full justify-center md:justify-start text-black ${typography.h3} text-base md:text-xl`}>
          {new Date(currentYear, currentMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </div>

        {/* Attendance and Shift Statistics Row */}
        <div className="w-full flex flex-col lg:flex-row gap-4 md:gap-6">
          {/* Attendance Chart */}
          <div className="w-full max-w-[160px] mx-auto lg:mx-0 lg:w-40 lg:h-40 flex-shrink-0 bg-[radial-gradient(ellipse_152.09%_184.58%_at_136.24%_-51.30%,_rgba(10,_128,_137,_0.09)_0%,_rgba(255,_255,_255,_0.72)_100%)] rounded-xl outline outline-1 outline-offset-[-1px] outline-neutral-300 flex items-center justify-center">
            <DonutChart
              percentage={employee.attendancePercentage}
              size={window.innerWidth < 768 ? 100 : 128}
              strokeWidth={12}
              centerTextClassName={`${typography.h2} text-black text-sm md:text-base`}
            />
          </div>
          
          {/* Shift Statistics */}
          <div className="w-full lg:w-80 lg:min-h-[160px] lg:flex-shrink-0 bg-[radial-gradient(ellipse_152.09%_184.58%_at_136.24%_-51.30%,_rgba(10,_128,_137,_0.09)_0%,_rgba(255,_255,_255,_0.72)_100%)] rounded-xl outline outline-1 outline-offset-[-1px] outline-neutral-300 p-4 flex flex-col justify-center">
            <div className="w-full flex justify-between items-center mb-3">
              <div className={`text-black ${typography.p} text-xs md:text-sm whitespace-nowrap`}>Total Shift assigned</div>
              <div className={`text-black ${typography.h3} text-base md:text-lg flex-shrink-0 ml-2`}>{employee.totalShifts}</div>
            </div>
            <div className="w-full flex justify-between items-center mb-3">
              <div className="flex justify-start items-center gap-2 md:gap-3 flex-1 min-w-0">
                <div className="w-5 h-5 md:w-7 md:h-7 bg-gradient-to-bl from-stone-400 to-lime-600 rounded-full flex-shrink-0" />
                <div className={`text-black ${typography.p} text-xs md:text-sm whitespace-nowrap`}>Total Present:</div>
              </div>
              <div className={`text-black ${typography.h3} text-base md:text-lg flex-shrink-0 ml-2`}>{employee.presentShifts}</div>
            </div>
            <div className="w-full flex justify-between items-center">
              <div className="flex justify-start items-center gap-2 md:gap-3 flex-1 min-w-0">
                <div className="w-5 h-5 md:w-7 md:h-7 bg-red-500 rounded-full flex-shrink-0" />
                <div className={`text-black ${typography.p} text-xs md:text-sm whitespace-nowrap`}>Total Absents:</div>
              </div>
              <div className={`text-black ${typography.h3} text-base md:text-lg flex-shrink-0 ml-2`}>{employee.absentShifts}</div>
            </div>
          </div>
        </div>
        
        {/* Task Statistics */}
        <div className="w-full bg-[radial-gradient(ellipse_152.09%_184.58%_at_136.24%_-51.30%,_rgba(10,_128,_137,_0.09)_0%,_rgba(255,_255,_255,_0.72)_100%)] rounded-xl outline outline-1 outline-offset-[-1px] outline-neutral-300 p-4">
          <div className="w-full inline-flex justify-between items-center mb-3">
            <div className="flex justify-start items-center gap-2 md:gap-3 min-w-0 flex-1">
              <img src={TdesignTaskAddFilled} alt="Total Tasks" className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0" />
              <div className={`justify-center text-black ${typography.p} text-xs md:text-sm`}>Total Tasks:</div>
            </div>
            <div className={`text-right justify-center text-black ${typography.h3} text-base md:text-lg flex-shrink-0 ml-2`}>{employee.totalTasks}</div>
          </div>
          <div className="w-full inline-flex justify-between items-center mb-3">
            <div className="flex justify-start items-center gap-2 md:gap-3 min-w-0 flex-1">
              <img src={IcBaselineTask} alt="Tasks On Time" className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0" />
              <div className={`justify-center text-black ${typography.p} text-xs md:text-sm`}>Tasks On Time:</div>
            </div>
            <div className={`text-right justify-center text-black ${typography.h3} text-base md:text-lg flex-shrink-0 ml-2`}>{employee.tasksOnTime}</div>
          </div>
          <div className="w-full inline-flex justify-between items-center">
            <div className="flex justify-start items-center gap-2 md:gap-3 min-w-0 flex-1">
              <img src={MaterialSymbolsStarRounded} alt="Task Score" className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0" />
              <div className={`justify-center text-black ${typography.p} text-xs md:text-sm`}>Task Score:</div>
            </div>
            <div className={`text-right justify-center text-black ${typography.h3} text-base md:text-lg flex-shrink-0 ml-2`}>{employee.taskScore}</div>
          </div>
        </div>
      </div>
    );
  };


  return (
    <div className="w-full h-full">
      {/* Selected Site Information */}
      <div className="bg-blue-50 rounded-xl p-4 md:p-6">
        
        {/* Year and Month Dropdown Pickers */}
        <div className="mb-4 md:mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
          <div className="flex flex-col gap-2">
            <label className={`${typography.p} text-black font-medium`}>
              Select Year:
            </label>
            <select
              value={currentYear}
              onChange={(e) => setCurrentYear(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              {Array.from({ length: 10 }, (_, i) => {
                const year = new Date().getFullYear() - 5 + i;
                return (
                  <option key={year} value={year}>
                    {year}
                  </option>
                );
              })}
            </select>
          </div>
          
          <div className="flex flex-col gap-2">
            <label className={`${typography.p} text-black font-medium`}>
              Select Month:
            </label>
            <select
              value={currentMonth}
              onChange={(e) => setCurrentMonth(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              {Array.from({ length: 12 }, (_, i) => {
                const month = i + 1;
                const monthName = new Date(2024, i).toLocaleDateString('en-US', { month: 'long' });
                return (
                  <option key={month} value={month}>
                    {monthName}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
        
        {/* Instructions Text with Custom h5 Typography */}
        <div className="mb-4">
          <h6 className={`${typography.h5} text-sm md:text-base`} style={{color: '#000000'}}>
            Please search and select the site to view best performers
          </h6>
        </div>
        
        {/* Search Bar Section */}
        <div className="flex items-center gap-2 md:gap-4 search-container">
          <div className="flex-1 relative">
            <div onClick={handleSearchBarClick}>
              <SearchBar
                placeholder="Select a site"
                value={searchTerm}
                onChange={handleSearchChange}
                onSearch={handleSearchSubmit}
                suggestions={searchSuggestions}
                onSuggestionClick={handleSuggestionClick}
                className="w-full max-w-full md:max-w-2xl ml-0 cursor-pointer" 
              />
            </div>
            {/* Custom dropdown for all sites */}
            {showAllSitesDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
                {sitesLoading ? (
                  <div className="p-4 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                    <div className={`mt-2 ${typography.p} text-gray-600`}>Loading sites...</div>
                  </div>
                ) : allSites.length > 0 ? (
                  <>
                    <div className="p-2 border-b border-gray-200 bg-gray-50">
                      <p className="text-sm font-medium text-gray-700">
                        All Sites ({allSites.length})
                      </p>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {searchSuggestions.map((siteName, index) => (
                        <div
                          key={index}
                          className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          onClick={() => handleSuggestionClick(siteName)}
                        >
                          <div className="font-medium text-gray-900">{siteName}</div>
                          {allSites.find(s => s.site_name === siteName)?.site_address && (
                            <div className="text-sm text-gray-500 truncate">
                              {allSites.find(s => s.site_name === siteName)?.site_address}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="p-4 text-center text-gray-500">
                    No sites available
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        

        {/* Site Details */}
        {/* <div className="flex justify-between items-start">
          <p className={typography.p} style={{color: '#000000'}}>
            {selectedSite ? `Selected Site: ${selectedSite.site_name}` : 'Selected Site:'}
          </p>
          {selectedSite && (
            <button
              onClick={clearSiteSelection}
              className="text-red-600 hover:text-red-800 text-sm font-medium"
            >
              Clear Selection
            </button>
          )}
        </div> */}
        {/* {selectedSite ? (
          <div className="space-y-2">
            <p className={typography.p} style={{color: '#000000'}}>
              <span className="font-semibold">Address:</span> {selectedSite.address}
            </p>
          </div>
        ) : (
          <p className={typography.p} style={{color: '#000000'}}>
            Please search and select a site above
          </p>
        )} */}
      </div>

      {/* Best Performer Heading */}
      {selectedSite && (
        <div className="mt-4 md:mt-8 px-2 md:px-0">
          <h3 className={`${typography.h3} text-lg md:text-2xl break-words`} style={{color: '#000000'}}>
            Best Performers for {selectedSite.site_name} - {new Date(currentYear, currentMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
          <p className={`${typography.p} text-sm md:text-base`} style={{color: '#666666', marginTop: '8px'}}>
            Showing all performers for the selected month/year
          </p>
        </div>
      )}

      {/* Best Performer Content */}
      {selectedSite && (
        <div className="mt-4 px-2 md:px-0">
          {/* Loading State */}
          {isLoading && (
            <div className="w-full inline-flex justify-center items-center gap-4 md:gap-6">
              <div className="flex items-center justify-center p-4 md:p-8">
                <div className="animate-spin rounded-full h-6 w-6 md:h-8 md:w-8 border-b-2 border-blue-600"></div>
                <span className={`ml-2 md:ml-3 ${typography.p} text-sm md:text-base text-black`}>Loading best performers...</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="w-full inline-flex justify-center items-center gap-4 md:gap-6">
              <div className="flex items-center justify-center p-4 md:p-8">
                <div className="text-red-600 text-center">
                  <div className={`${typography.h4} text-base md:text-lg text-red-600 mb-2`}>Error Loading Data</div>
                  <div className={`${typography.p} text-sm md:text-base text-red-500`}>{error}</div>
                  <button 
                    onClick={() => fetchBestPerformerData(currentMonth, currentYear)}
                    className="mt-4 px-4 py-2 text-sm md:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Data Content */}
          {!isLoading && !error && (
            <div className="w-full md:max-w-[972px] inline-flex flex-col md:flex-row justify-start items-start gap-4 md:gap-6">
              <div className="w-full md:w-auto inline-flex flex-col justify-start items-start gap-3 md:gap-4">
                {bestPerformers.length > 0 ? (
                bestPerformers.map((employee) => {
                  const isSelected = selectedEmployee && selectedEmployee.id === employee.id;
                  const CardComponent = isSelected ? Card.Gradient : Card.Glassmorphism;
                  
                  return (
                    <CardComponent 
                      key={employee.id}
                      className="w-full md:w-96 p-3 md:p-3 inline-flex justify-start items-center gap-3 md:gap-6 cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => handleEmployeeClick(employee)}
                    >
                      <ProfileImage
                        userId={employee.id}
                        userName={employee.userName || employee.name}
                        imagePath={employee.profileImageUrl}
                        size="md"
                        className="w-12 h-12 md:w-14 md:h-14 flex-shrink-0"
                      />
                      <div className="flex-1 inline-flex flex-col justify-start items-start min-w-0">
                        <div className={`${typography.h5} text-sm md:text-base text-black truncate w-full`}>{employee.name}</div>
                        <div className={`${typography.p} text-xs md:text-sm text-black`}>ID : {employee.employeeId}</div>
                        <div className={`${typography.p} text-xs md:text-sm text-black`}>Role: {employee.role}</div>
                        <div className={`${typography.p} text-xs md:text-sm text-black`}>Score: {employee.totalScore}</div>
                      </div>
                    </CardComponent>
                  );
                })
              ) : (
                <div className="w-full md:w-96 p-4 md:p-6 inline-flex flex-col justify-center items-center gap-3 md:gap-4">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 md:w-8 md:h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                  </div>
                  <div className={`${typography.p} text-sm md:text-base text-black text-center`}>
                    No best performers found for {new Date(currentYear, currentMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </div>
                  <div className={`${typography.p} text-xs md:text-sm text-gray-500 text-center`}>
                    Try selecting a different month or year
                  </div>
                </div>
              )}
              </div>
              {/* Desktop View - Employee Details Panel */}
              <div className="hidden md:flex flex-1 min-h-[400px] md:h-auto lg:h-[580px] relative bg-neutral-50/50 rounded-xl shadow-[-2px_-2px_6px_0px_rgba(211,211,211,0.10)] shadow-[2px_2px_6px_0px_rgba(211,211,211,0.10)] outline outline-1 outline-offset-[-1px] outline-neutral-300 backdrop-blur-[20px] overflow-hidden">
                {selectedEmployee ? (
                  renderEmployeeDetails(selectedEmployee)
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className={`text-center text-black ${typography.h4}`}>
                      Select an employee to view details
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Mobile Modal */}
          {showMobileModal && selectedEmployee && createPortal(
            <div 
              className="fixed inset-0 z-[9999] flex items-center justify-center min-h-screen w-full overflow-y-auto p-2 sm:p-4 bg-black/50" 
              onClick={() => setShowMobileModal(false)} 
              role="dialog" 
              aria-modal="true"
            >
              <div 
                className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col my-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-3 sm:p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                  <h2 className={`${typography.h4} text-base sm:text-lg text-black`}>
                    Employee Details
                  </h2>
                  <button
                    type="button"
                    onClick={() => setShowMobileModal(false)}
                    className="text-gray-500 hover:text-black p-1 rounded hover:bg-gray-100"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                </div>
                <div className="p-3 sm:p-4 overflow-auto flex-1 min-h-0">
                  <div className="relative bg-neutral-50/50 rounded-xl p-3 sm:p-4 space-y-3 sm:space-y-4">
                    {renderEmployeeDetails(selectedEmployee)}
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>
      )}
    </div>
  );
};

export default BestPerformer;
